import { CheckCircle2, FileClock, FileText, FileX2, Inbox, Plus, RefreshCw, Send, Undo2, XCircle } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Pager } from '@/components/common/Pager'
import { InfoNote } from '@/components/common/Panel'
import { PageHeader } from '@/components/common/PageHeader'
import { EmptyState, ErrorState } from '@/components/common/States'
import { usePagination } from '@/components/common/usePagination'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { confirmModal } from '@/components/ui/modal'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAuth } from '@/lib/auth/AuthProvider'
import { fmtDate } from '../shared/format'
import { useEDocs, useSetEDocStatus, useTransactions } from './api'
import { EDocCreateModal } from './components/EDocCreateModal'
import { EDocStatusBadge, Kpi, Money, Section } from './components/FinanceBits'
import { FINANCE_EDOC_WRITE } from './permissions'
import { EDOC_STATUSES, type EDocStatus, type EDocument } from './types'
import { toastError, useFinanceLookups } from './ui'

type Action = { to: EDocStatus; key: 'send' | 'query' | 'cancel' | 'redraft'; icon: typeof Send; destructive?: boolean }

function actionsFor(d: EDocument): Action[] {
  if (d.direction === 'incoming') return []
  switch (d.status) {
    case 'draft':
      return [{ to: 'sent', key: 'send', icon: Send }, { to: 'cancelled', key: 'cancel', icon: XCircle, destructive: true }]
    case 'sent':
      return [{ to: 'accepted', key: 'query', icon: RefreshCw }, { to: 'cancelled', key: 'cancel', icon: XCircle, destructive: true }]
    case 'accepted':
      return [{ to: 'cancelled', key: 'cancel', icon: XCircle, destructive: true }]
    case 'rejected':
      return [{ to: 'draft', key: 'redraft', icon: Undo2 }]
    default:
      return []
  }
}

export function EDocumentsPage() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const docs = useEDocs()
  const txs = useTransactions()
  const setStatus = useSetEDocStatus()
  const { partyName } = useFinanceLookups()
  const [direction, setDirection] = useState<'outgoing' | 'incoming'>('outgoing')
  const [status, setStatusFilter] = useState<EDocStatus | 'all'>('all')
  const [creating, setCreating] = useState(false)
  const canWrite = can(FINANCE_EDOC_WRITE)

  const all = docs.data ?? []
  const out = all.filter((d) => d.direction === 'outgoing')
  const rows = all.filter((d) => d.direction === direction && (status === 'all' || d.status === status))
  const count = (s: EDocStatus) => out.filter((d) => d.status === s).length
  const busy = new Set(all.filter((d) => d.status !== 'cancelled' && d.status !== 'rejected').map((d) => d.transactionId))
  const candidates = (txs.data ?? []).filter((x) => x.kind === 'income' && !busy.has(x.id))

  const run = async (d: EDocument, a: Action) => {
    if (a.key !== 'query') {
      const ok = await confirmModal({
        title: t(`finance.edoc.confirm.${a.key}Title`),
        description: t(`finance.edoc.confirm.${a.key}Body`, { number: d.number, party: partyName(d.counterpartyId) }),
        confirmLabel: t(`finance.edoc.actions.${a.key}`),
        destructive: a.destructive,
      })
      if (!ok) return
    }
    setStatus.mutate({ id: d.id, status: a.to }, { onSuccess: () => toast.success(t(`finance.edoc.done.${a.key}`)), onError: toastError })
  }

  const { pageRows, pager } = usePagination(rows, [direction, status])
  return (
    <>
      <PageHeader
        eyebrow={t('nav.groups.finance')}
        title={t('finance.edoc.title')}
        description={t('finance.edoc.subtitle')}
        actions={
          canWrite ? (
            <Button onClick={() => setCreating(true)} disabled={!candidates.length}>
              <Plus />
              {t('finance.edoc.new')}
            </Button>
          ) : null
        }
      />
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
          <Kpi icon={FileClock} tone="neutral" label={t('finance.edoc.status.draft')} value={count('draft')} />
          <Kpi icon={Send} tone="info" label={t('finance.edoc.status.sent')} value={count('sent')} />
          <Kpi icon={CheckCircle2} tone="success" label={t('finance.edoc.status.accepted')} value={count('accepted')} />
          <Kpi icon={FileX2} tone="danger" label={t('finance.edoc.status.rejected')} value={count('rejected')} />
        </div>
        <InfoNote>{t('finance.edoc.info')}</InfoNote>

        <Section
          title={t('finance.edoc.list')}
          icon={FileText}
          bodyClassName="p-0"
          actions={
            <div className="flex flex-wrap items-center gap-2">
              <Tabs value={direction} onValueChange={(v) => setDirection(v as typeof direction)}>
                <TabsList size="sm">
                  <TabsTrigger value="outgoing">
                    <Send />
                    {t('finance.edoc.outgoing')}
                  </TabsTrigger>
                  <TabsTrigger value="incoming">
                    <Inbox />
                    {t('finance.edoc.incoming')}
                  </TabsTrigger>
                </TabsList>
              </Tabs>
              <Select
                size="sm"
                className="w-40"
                value={status}
                onChange={(v) => setStatusFilter(v as EDocStatus | 'all')}
                aria-label={t('finance.tx.status')}
                options={[{ value: 'all', label: t('common.all') }, ...EDOC_STATUSES.map((s) => ({ value: s, label: t(`finance.edoc.status.${s}`) }))]}
              />
            </div>
          }
        >
          {docs.isError ? (
            <ErrorState error={docs.error} onRetry={() => void docs.refetch()} />
          ) : docs.isLoading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 5 }, (_, i) => (
                <Skeleton key={i} className="h-14" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <EmptyState icon={FileText} title={t('finance.edoc.empty')} />
          ) : (
            <>
              <ul className="divide-y">
                {pageRows.map((d) => (
                  <li key={d.id} className="flex flex-col gap-2 px-4 py-3 lg:flex-row lg:items-center lg:gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-mono text-sm font-medium">{d.number}</span>
                        <Badge tone="outline" className="rounded-md">{t(`finance.edoc.types.${d.type}`)}</Badge>
                        <EDocStatusBadge status={d.status} />
                      </div>
                      <p className="truncate text-sm">{partyName(d.counterpartyId)}</p>
                      <p className="truncate text-xs text-muted-foreground">
                        {fmtDate(d.date)} · ETTN {d.ettn}
                      </p>
                      {d.statusNote ? <p className="text-xs text-destructive">{d.statusNote}</p> : null}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 lg:justify-end">
                      <Money value={d.total} className="mr-2 font-semibold" />
                      {canWrite
                        ? actionsFor(d).map((a) => (
                            <Button
                              key={a.key}
                              size="sm"
                              variant={a.key === 'send' ? 'default' : 'outline'}
                              className={a.destructive ? 'text-destructive hover:text-destructive' : undefined}
                              disabled={setStatus.isPending}
                              onClick={() => void run(d, a)}
                            >
                              <a.icon />
                              {t(`finance.edoc.actions.${a.key}`)}
                            </Button>
                          ))
                        : null}
                    </div>
                  </li>
                ))}
              </ul>
              <Pager {...pager} />
            </>
          )}
        </Section>
      </div>
      <EDocCreateModal transaction={null} candidates={creating ? candidates : undefined} onClose={() => setCreating(false)} />
    </>
  )
}
